import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Subscription } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import { catchError, from, of, switchMap } from 'rxjs';
import { ProductIdeaFilter } from './filter/ProductIdeaFilter.js';
import { Partner } from './master-data-management/partner/entities/Partner.js';
import { ChancenwerkUser } from './master-data-management/users/entities/ChancenwerkUser.js';

/**
 * @entity ProductIdea
 */
@Entity()
export class ProductIdea extends MongoEntity {
  public static override _class = 'ProductIdea';
  public static override _collectionName = 'product-ideas';

  @validate(nonOptional())
  @Field(() => String)
  public name: Maybe<string>;

  @Field(() => String)
  public description: Maybe<string>;

  @Field(() => DateTime, { default: v => v ?? DateTime.now() })
  public createdAt: Maybe<DateTime>;

  @Field(() => ID)
  public createdByID: Maybe<string>;

  @Field(() => String, {
    resolve: function (this: ProductIdea) {
      return async () => {
        if (!this.createdByID) return null;
        const collection = await getRawCollection<ChancenwerkUser>(ChancenwerkUser._collectionName);
        return (constructValue(await collection.findOne({ _id: this.createdByID })) as ChancenwerkUser).toString();
      };
    },
  })
  public createdByName: Maybe<string>;

  @Field(() => ID)
  public partnerID: Maybe<string>;

  @DataLoadedField(() => Partner, ({ partnerID }) => partnerID)
  public partner: Maybe<Partner>;

  @Subscription(() => [ProductIdea])
  static subscribeProductIdeasByFilterPaginated(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => ProductIdeaFilter) filter: ProductIdeaFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<ProductIdea>) => {
      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<ProductIdea>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            skip,
            limit,
            sort: { kuerzel: -1 },
          });
        }),
        catchError(err => {
          logger.error({ err }, 'Error during subscription');
          return of([] as ProductIdea[]);
        }),
      );
    };
  }

  @Subscription(() => [ProductIdea])
  static subscribeProductIdeasForPartner(@Arg('partnerID', () => String) partnerID: string) {
    return (gqlFields: BaseQLSelectionSet<ProductIdea>) => {
      const selector = { partnerID };
      const handle = getCollectionHandle<ProductIdea>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            sort: { kuerzel: -1 },
          });
        }),
        catchError(err => {
          logger.error({ err }, 'Error during subscription');
          return of([] as ProductIdea[]);
        }),
      );
    };
  }

  override toString() {
    return this.name;
  }
}
